/**
 * Opening balances.
 *
 * Two things make this screen correct where a per-ledger field was not:
 *
 *  1. The difference is shown live and Save stays disabled until it is
 *     zero. Double entry is enforced here exactly as it is for every other
 *     voucher, so the books cannot be tilted by a typo.
 *  2. Each account defaults to the side it naturally sits on -- capital in
 *     Credit, cash in Debit. Inverted opening balances are otherwise the
 *     single most common data-entry error on this screen.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
  fetchOpening, importLegacyOpening, saveOpening,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type DrCr, type OpeningAccount } from '../api/types'
import { useAuth } from '../store/AuthContext'
import SectionHead from '../components/SectionHead'
import { addMoney, display, normalise, sanitize, subMoney } from '../vouchers/money'
import { c } from '../theme'

interface Row { amount: string; dr_cr: DrCr }

const GRID = '1fr 96px 130px 130px'

export default function OpeningBalances() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const qc = useQueryClient()
  const editable = can(P.adminPeriods)

  const [rows, setRows] = useState<Record<string, Row>>({})
  const [filter, setFilter] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [legacy, setLegacy] = useState<{ converted: number; difference: string } | null>(null)

  const q = useQuery({ queryKey: ['opening'], queryFn: fetchOpening })

  useEffect(() => {
    if (!q.data) return
    const seed: Record<string, Row> = {}
    for (const a of q.data.accounts) {
      seed[a.ledger_code] = { amount: Number(a.amount) ? a.amount : '',
                              dr_cr: a.dr_cr }
    }
    setRows(seed)
  }, [q.data])

  const totals = useMemo(() => {
    const dr = addMoney(...Object.values(rows)
      .filter((r) => r.dr_cr === 'D').map((r) => r.amount || '0'))
    const cr = addMoney(...Object.values(rows)
      .filter((r) => r.dr_cr === 'C').map((r) => r.amount || '0'))
    return { dr, cr, diff: subMoney(dr, cr) }
  }, [rows])

  const balanced = normalise(totals.diff) === '0.00'
  const anyEntered = Object.values(rows).some((r) => r.amount && Number(r.amount))

  const save = useMutation({
    mutationFn: (toSuspense: boolean) => saveOpening(
      Object.entries(rows)
        .filter(([, r]) => r.amount && normalise(r.amount) !== '0.00')
        .map(([ledger_code, r]) => ({
          ledger_code, amount: normalise(r.amount), dr_cr: r.dr_cr })),
      toSuspense),
    onSuccess: () => {
      setSaved(true); setError(null)
      void qc.invalidateQueries({ queryKey: ['opening'] })
      void qc.invalidateQueries({ queryKey: ['trial-balance'] })
      void qc.invalidateQueries({ queryKey: ['balance-sheet'] })
      void qc.invalidateQueries({ queryKey: ['outstanding'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const runLegacy = useMutation({
    mutationFn: (dry: boolean) => importLegacyOpening(dry),
    onSuccess: (r) => {
      if (r.dry_run) setLegacy({ converted: r.converted, difference: r.difference })
      else { setLegacy(null); void qc.invalidateQueries({ queryKey: ['opening'] }) }
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const visible = (q.data?.accounts ?? []).filter((a) => {
    if (!filter.trim()) return true
    const f = filter.toLowerCase()
    return a.ledger_name.toLowerCase().includes(f) ||
           a.ledger_code.toLowerCase().includes(f)
  })

  function set(code: string, patch: Partial<Row>) {
    setRows((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }))
    setSaved(false)
  }

  if (q.isLoading) {
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Skeleton /><Skeleton /><Skeleton /></Paper>
  }

  return (
    <Paper variant="outlined">
      <SectionHead label={t('opening.title')}
        right={q.data?.exists ? (
          <Chip size="small" variant="outlined" label={q.data.voucher_no} />
        ) : undefined} />

      <Stack spacing={1.5} sx={{ px: 2, pb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t('opening.explain', { date: q.data?.as_of })}
        </Typography>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {saved && <Alert severity="success" onClose={() => setSaved(false)}>
          {t('opening.saved')}</Alert>}

        <Stack direction="row" spacing={1.5} alignItems="center"
          className="no-print" useFlexGap flexWrap="wrap">
          <TextField size="small" placeholder={t('opening.filter')} value={filter}
            onChange={(e) => setFilter(e.target.value)} sx={{ width: 240 }} />
          <Box sx={{ flex: 1 }} />
          {editable && (
            <Button size="small" sx={{ color: c.inkMuted }}
              onClick={() => runLegacy.mutate(true)}>
              {t('opening.importLegacy')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1, px: 2,
        pb: 0.5, borderBottom: `1px solid ${c.border}` }}>
        <span className="section-label">{t('report.account')}</span>
        <span className="section-label">{t('entry.drcr')}</span>
        <span className="section-label" style={{ textAlign: 'end' }}>
          {t('entry.debit')}</span>
        <span className="section-label" style={{ textAlign: 'end' }}>
          {t('entry.credit')}</span>
      </Box>

      <Box className="ruled" sx={{ maxHeight: 460, overflowY: 'auto' }}>
        {visible.map((a: OpeningAccount) => {
          const r = rows[a.ledger_code] ?? { amount: '', dr_cr: a.natural_side }
          return (
            <Box key={a.ledger_code} sx={{
              display: 'grid', gridTemplateColumns: GRID, gap: 1,
              px: 2, py: 0.65, alignItems: 'center',
              '&:focus-within': { bgcolor: c.primarySoft } }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5 }} noWrap>{a.ledger_name}</Typography>
                <span className="num" style={{ fontSize: 11, color: c.inkMuted,
                  textAlign: 'start' }}>{a.ledger_code}</span>
              </Box>

              <Box role="button" tabIndex={-1}
                onClick={() => editable && set(a.ledger_code,
                  { dr_cr: r.dr_cr === 'D' ? 'C' : 'D' })}
                sx={{
                  fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600,
                  cursor: editable ? 'pointer' : 'default', userSelect: 'none',
                  color: c.inkMuted,
                }}>
                {r.dr_cr === 'D' ? 'Dr' : 'Cr'}
              </Box>

              {(['D', 'C'] as const).map((side) => (
                <Box key={side}>
                  {r.dr_cr === side && (
                    <input className="num" inputMode="decimal" disabled={!editable}
                      value={r.amount}
                      onChange={(e) => set(a.ledger_code,
                        { amount: sanitize(e.target.value) })}
                      onBlur={() => set(a.ledger_code,
                        { amount: r.amount ? normalise(r.amount) : '' })}
                      onFocus={(e) => e.currentTarget.select()}
                      style={{
                        width: '100%', height: 28, padding: '0 6px',
                        border: `1px solid ${c.border}`, borderRadius: 4,
                        font: 'inherit', fontFamily: 'var(--mono)',
                        textAlign: 'right', outline: 'none',
                        background: editable ? c.surface : 'transparent',
                      }} />
                  )}
                </Box>
              ))}
            </Box>
          )
        })}
      </Box>

      {/* Live difference -- Save stays shut until this is zero. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1,
        px: 2, py: 1.25, borderTop: `1px solid ${c.ink}` }}>
        <span className="section-label">{t('report.total')}</span>
        <Box />
        <span className="num" style={{ fontWeight: 600 }}>{display(totals.dr)}</span>
        <span className="num" style={{ fontWeight: 600 }}>{display(totals.cr)}</span>
      </Box>

      {!balanced && anyEntered && (
        <Alert severity="warning" sx={{ mx: 2, mb: 1.5 }}>
          {t('opening.outOfBalance', { amount: display(totals.diff) })}
        </Alert>
      )}

      {editable && (
        <Stack direction="row" spacing={1.5} alignItems="center"
          sx={{ px: 2, pb: 2 }} useFlexGap flexWrap="wrap">
          <Button variant="contained" disabled={!anyEntered || !balanced || save.isPending}
            onClick={() => save.mutate(false)}>
            {save.isPending ? t('common.loading') : t('common.save')}
          </Button>
          {!balanced && anyEntered && (
            <Button color="warning" disabled={save.isPending}
              onClick={() => save.mutate(true)}>
              {t('opening.postDifference')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('opening.differenceHint')}
          </Typography>
        </Stack>
      )}

      <Dialog open={!!legacy} onClose={() => setLegacy(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('opening.importLegacy')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('opening.legacyFound', { n: legacy?.converted ?? 0 })}
          </Typography>
          {legacy && Number(legacy.difference) !== 0 && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              {t('opening.legacyDifference', {
                amount: display(legacy.difference) })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setLegacy(null)}>
            {t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => runLegacy.mutate(false)}>
            {t('opening.convert')}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
